import { useContext, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { archiveItemAPI, copyItemAPI, createItemAPI, updateItemAPI, uploadItemImageAPI } from "../../../utils/api/menu";
import { formatDiscount, pricingLabel } from "../../../utils/menuPricing";
import { NoticeBox } from "../../ui";
import { FoodItemFields, itemFormError } from "../foodMenu/FoodItemForm/FoodItemForm";
import { emptyItem, formToPayload, itemToForm, samePayload } from "../foodMenu/FoodItemForm/itemFormModel";
import { menuErrorMessage } from "../menuRole";
import { topActionsText } from "./topActionsText";
import { ActionDialog, CopyLinksPreview, DialogFooter, ItemImageField, ItemPicker, MenuPicker, RestaurantPicker, SectionPicker, bothNames, copyResultMessage, findItem, findSection, useCopyPreview, useCopyRestaurants, useDropInvalid, useMenuDetail, useRestaurantAdditions, useRestaurantMenus } from "./shared";

// Item → Add / Update / Copy / Archive from the top of the Menu page.
// This component only orchestrates selection + submission; the item fields
// are the same FoodItemFields the per-card editor uses.
//   add     : Menu → Section → item fields → save (+ optional image upload)
//   update  : Menu → Section → Item → item fields (prefilled) → save
//   copy    : source Restaurant → Menu → Section → Item,
//             destination Restaurant → Menu → Section (any restaurant the
//             backend lists for the user; cross-restaurant links previewed)
//   archive : Menu → Section → Item → confirm
export default function ItemActionDialog({ mode, menus, restaurant, initialMenuId, onClose, onDone }) {
  const { can } = useAccess();
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const isCopy = mode === "copy";
  const currentRestaurantId = restaurant?._id ? String(restaurant._id) : "";
  // Copy sources may be any menu of the restaurant; everything else (and
  // every destination) must be a menu that can still be changed.
  const isEditable = (id) => menus.some((m) => String(m._id) === String(id) && m.lifecycle !== "archived");
  const [menuId, setMenuId] = useState(mode === "copy" || isEditable(initialMenuId) ? initialMenuId || "" : "");
  const [sectionId, setSectionId] = useState("");
  const [itemId, setItemId] = useState("");
  const [destMenuId, setDestMenuId] = useState(isEditable(initialMenuId) ? initialMenuId : "");
  const [destSectionId, setDestSectionId] = useState("");
  const [srcRestaurantId, setSrcRestaurantId] = useState(currentRestaurantId);
  const [destRestaurantId, setDestRestaurantId] = useState(currentRestaurantId);
  const [additionMap, setAdditionMap] = useState({});
  const [data, setData] = useState(mode === "add" ? emptyItem() : null);
  const [original, setOriginal] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Copy: each side picks its restaurant first; menus follow the restaurant.
  const copyRestaurants = useCopyRestaurants(restaurant, isCopy);
  // Source AND destination restaurant of a copy are both checked (NA-011): the ids the
  // pickers hold are mapped to public ids through the list the backend returned.
  const { canFor: canCopyFor } = useRestaurantAccess(copyRestaurants.restaurants);
  const srcMenus = useRestaurantMenus(isCopy ? srcRestaurantId : "", { currentRestaurantId, currentMenus: menus, includeArchived: true });
  const destMenus = useRestaurantMenus(isCopy ? destRestaurantId : "", { currentRestaurantId, currentMenus: menus });
  const crossRestaurant = isCopy && Boolean(srcRestaurantId && destRestaurantId) && srcRestaurantId !== destRestaurantId;

  const { menu, loading } = useMenuDetail(menuId);
  const dest = useMenuDetail(isCopy && destMenuId !== menuId ? destMenuId : "");
  const destMenu = isCopy ? (destMenuId === menuId ? menu : dest.menu) : null;
  const section = findSection(menu, sectionId);
  const item = findItem(section, itemId);
  const additions = useRestaurantAdditions(menu?.restaurant?._id);

  // Cascade: stale ids never survive a parent change or a reload.
  const sectionIds = useMemo(() => (menu?.sections || []).map((s) => String(s._id)), [menu]);
  const itemIds = useMemo(() => (section?.items || []).map((i) => String(i._id)), [section]);
  const destSectionIds = useMemo(() => (destMenu?.sections || []).map((s) => String(s._id)), [destMenu]);
  useDropInvalid(sectionId, sectionIds, () => setSectionId(""), Boolean(menu));
  useDropInvalid(itemId, itemIds, () => selectItem(""), Boolean(section));
  useDropInvalid(destSectionId, destSectionIds, () => setDestSectionId(""), Boolean(destMenu));

  // Copy cascade: a restaurant change clears that side's menu/section/item,
  // a menu change clears its section. Stale ids never survive.
  const selectSrcRestaurant = (id) => {
    setSrcRestaurantId(id);
    selectMenu("");
  };
  const selectDestRestaurant = (id) => {
    setDestRestaurantId(id);
    setDestMenuId("");
    setDestSectionId("");
    setAdditionMap({});
    setError("");
  };
  const selectDestMenu = (id) => {
    setDestMenuId(id);
    setDestSectionId("");
  };

  const copyRequest =
    isCopy && item && destMenuId && destSectionId
      ? { source: { restaurantId: srcRestaurantId, menuId, sectionId, itemId }, destination: { restaurantId: destRestaurantId, menuId: destMenuId, sectionId: destSectionId }, ...(Object.keys(additionMap).length ? { additionMap } : {}) }
      : null;
  const copyPreview = useCopyPreview(copyRequest && { type: "item", ...copyRequest }, crossRestaurant);
  const selectMenu = (id) => {
    setMenuId(id);
    setSectionId("");
    selectItem("");
    setError("");
  };
  const selectSection = (id) => {
    setSectionId(id);
    selectItem("");
  };
  function selectItem(id) {
    setItemId(id);
    setFile(null);
    setAdditionMap({});
    if (mode !== "update") return;
    const picked = findItem(section, id);
    const form = picked ? itemToForm(picked) : null;
    setData(form);
    setOriginal(form);
  }

  const title = { add: t.addItem, update: t.updateItem, copy: t.copyItem, archive: t.archiveItem }[mode];
  const ready = {
    add: menuId && sectionId && data,
    update: menuId && sectionId && item && data,
    copy: copyRequest && (!crossRestaurant || copyPreview.preview),
    archive: item,
  }[mode];

  const run = async (action) => {
    if (busy) return;
    if (!can("menu.manage", { restaurantId: restaurant?.id })) return;
    if (mode === "copy" && !(canCopyFor("menu.manage", srcRestaurantId) && canCopyFor("menu.manage", destRestaurantId))) {
      setError(menuErrorMessage({ status: 403 }, lang));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    } finally {
      setBusy(false);
    }
  };

  // The image goes through the upload endpoint after the item exists; a
  // failed upload keeps the saved item and says so.
  const withImage = async (targetItemId, result, message) => {
    if (!file) return onDone({ menuId, menu: result.menu, message });
    try {
      const uploaded = await uploadItemImageAPI(menuId, targetItemId, file);
      return onDone({ menuId, menu: uploaded.menu, message });
    } catch (err) {
      return onDone({ menuId, menu: result.menu, message: `${t.imageUploadFailed}${menuErrorMessage(err, lang)}`, type: "error" });
    }
  };

  const submit = () =>
    run(async () => {
      if (mode === "add" || mode === "update") {
        const problem = itemFormError(data, lang);
        if (problem) return setError(problem);
      }
      if (mode === "add") {
        const result = await createItemAPI(menuId, sectionId, formToPayload(data));
        return withImage(result.itemId, result, t.itemAdded);
      }
      if (mode === "update") {
        const changed = !samePayload(data, original);
        if (!changed && !file) return setError(t.nothingChanged);
        // Only a complete payload is sent (all fields prefilled from the
        // current item), so nothing the user did not touch is replaced.
        const result = changed ? await updateItemAPI(menuId, itemId, formToPayload(data), sectionId) : { menu: null };
        return withImage(itemId, result, t.itemUpdated);
      }
      if (mode === "copy") {
        const result = await copyItemAPI(copyRequest);
        return onDone({ menuId: destMenuId, menu: result.menu, message: copyResultMessage(t, t.itemCopied, result) });
      }
      const result = await archiveItemAPI(menuId, itemId, sectionId);
      return onDone({ menuId, menu: result.menu, message: t.itemArchived });
    });

  const editable = menus.filter((m) => m.lifecycle !== "archived");
  const destEditable = destMenus.menus.filter((m) => m.lifecycle !== "archived");
  const showFields = (mode === "add" && sectionId) || (mode === "update" && item && data);

  return (
    <ActionDialog
      title={title}
      wide={mode === "add" || mode === "update"}
      busy={busy}
      error={error}
      onClose={onClose}
      footer={<DialogFooter submitLabel={{ add: t.save, update: t.save, copy: t.copy, archive: t.archive }[mode]} tone={mode === "archive" ? "danger" : "primary"} onSubmit={submit} onCancel={onClose} busy={busy} disabled={!ready} />}
    >
      {!isCopy && !editable.length && <NoticeBox variant="neutral" size="sm">{t.noMenus}</NoticeBox>}
      {isCopy && <h4 className="menu-action-step">{t.source}</h4>}
      <div className="menu-action-pickers">
        {isCopy && <RestaurantPicker label={t.sourceRestaurant} restaurants={copyRestaurants.restaurants} loading={copyRestaurants.loading} value={srcRestaurantId} onChange={selectSrcRestaurant} disabled={busy} />}
        <MenuPicker label={isCopy ? t.sourceMenu : t.menu} menus={isCopy ? srcMenus.menus : editable} value={menuId} onChange={selectMenu} disabled={busy || (isCopy && srcMenus.loading)} />
        <SectionPicker label={mode === "copy" ? t.sourceSection : t.section_} menu={menu} loading={loading} value={sectionId} onChange={selectSection} disabled={busy} />
        {mode !== "add" && <ItemPicker label={mode === "copy" ? t.sourceItem : t.item_} section={section} value={itemId} onChange={selectItem} disabled={busy} />}
      </div>

      {mode === "copy" && (
        <>
          <h4 className="menu-action-step">{t.destination}</h4>
          <div className="menu-action-pickers">
            <RestaurantPicker label={t.destinationRestaurant} restaurants={copyRestaurants.restaurants} loading={copyRestaurants.loading} value={destRestaurantId} onChange={selectDestRestaurant} disabled={busy} />
            <MenuPicker label={t.destinationMenu} menus={destEditable} value={destMenuId} onChange={selectDestMenu} disabled={busy || destMenus.loading} />
            <SectionPicker label={t.destinationSection} menu={destMenu} loading={dest.loading} value={destSectionId} onChange={setDestSectionId} disabled={busy} />
          </div>
          {destRestaurantId && !destMenus.loading && !destEditable.length && <NoticeBox variant="neutral" size="sm">{t.noMenusInRestaurant}</NoticeBox>}
          {crossRestaurant && copyRequest ? (
            <CopyLinksPreview preview={copyPreview.preview} loading={copyPreview.loading} error={copyPreview.error} disabled={busy} onMap={(sourceId, destId) => setAdditionMap((prev) => ({ ...prev, [sourceId]: destId }))} />
          ) : (
            <p className="menu-note">{t.copyItemNote}</p>
          )}
        </>
      )}

      {(mode === "archive" || mode === "copy") && item && <ItemSummary item={item} section={section} />}
      {mode === "archive" && item && (
        <NoticeBox variant="danger" size="sm">
          {t.archiveItemBody}
        </NoticeBox>
      )}

      {showFields && (
        <>
          <FoodItemFields itemData={data} setItemData={setData} menuAdditions={additions} />
          <ItemImageField current={mode === "update" ? item?.image : ""} file={file} onFile={setFile} disabled={busy} />
        </>
      )}
    </ActionDialog>
  );
}

// Enough detail to be sure it is the right item.
function ItemSummary({ item, section }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  return (
    <div className="menu-action-summary">
      {item.image ? <img src={item.image} alt="" className="menu-action-image-preview" /> : null}
      <div>
        <strong>{bothNames(item.name)}</strong>
        <div className="menu-secondary">
          {t.section_}: {bothNames(section?.name)}
        </div>
        <div className="menu-secondary">
          {t.prices}: {(item.pricing || []).map((p) => `${pricingLabel(p.name, lang)} ${p.price}${p.discount ? ` (−${formatDiscount(p.discount, lang)})` : ""}`).join(" · ") || "—"}
        </div>
      </div>
    </div>
  );
}
